import { Card, Divider, Empty, Input, Space, Typography } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { DndContext } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import styles from "./statusList.module.scss";
import { SubStatusMenuItem } from "./subStatusMenuItem/subStatusMenuItem";
import { SortableStatusItem } from "./sortableStatusItem/sortableStatusItem";
import { isSameSubStatus } from "../../utils";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { usePageFilter } from "../../../../../../hooks/usePageFilters";
import { useStatusReorder } from "../../hooks/useStatusReorder";
import { getCandidateStatusKey } from "../../../../../../utilities/candidate";

const { Title } = Typography;

export const StatusList = ({
  data,
  selectedSubStatus,
  onSubStatusSelected,
  expandedStatusIds,
  onStatusExpanded,
  onPositionUpdate,
}) => {
  const assessmentStatusId = useMemo(
    () => data.find((s) => s.name === "Assessment")?.id ?? null,
    [data],
  );
  const [debouncedSearchTerm, setDebouncedSearchTerm] = usePageFilter(
    "candidate_workflow_statuses_search",
    "",
  );

  const statusRefsMap = useRef({});
  const scrollContainerRef = useRef(null);
  const prevExpandedStatusIdsRef = useRef(null);
  const isInitialMountRef = useRef(true);

  const isDndEnabled = !!onPositionUpdate && !debouncedSearchTerm.trim();
  const [isDragActive, setIsDragActive] = useState(false);

  const { sensors, collisionDetection, handleDragEnd } = useStatusReorder(
    data,
    onPositionUpdate,
  );

  if (prevExpandedStatusIdsRef.current === null) {
    prevExpandedStatusIdsRef.current = [...expandedStatusIds];
  }

  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return undefined;
    }

    const prevExpandedIds = prevExpandedStatusIdsRef.current;

    const newlyExpandedId = expandedStatusIds.find(
      (id) => !prevExpandedIds.includes(id),
    );

    prevExpandedStatusIdsRef.current = [...expandedStatusIds];

    if (newlyExpandedId) {
      const timeoutId = setTimeout(() => {
        const statusElement = statusRefsMap.current[newlyExpandedId];
        const scrollContainer = scrollContainerRef.current;

        if (statusElement && scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = statusElement.getBoundingClientRect();
          const relativeTop =
            elementRect.top - containerRect.top + scrollContainer.scrollTop;

          scrollContainer.scrollTo({
            top: relativeTop - 16,
            behavior: "smooth",
          });
        }
      }, 300);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, [expandedStatusIds]);

  useEffect(() => {
    if (selectedSubStatus && selectedSubStatus.statusId) {
      const timeoutId = setTimeout(() => {
        const statusElement = statusRefsMap.current[selectedSubStatus.statusId];
        const scrollContainer = scrollContainerRef.current;

        if (statusElement && scrollContainer) {
          const containerRect = scrollContainer.getBoundingClientRect();
          const elementRect = statusElement.getBoundingClientRect();
          const relativeTop =
            elementRect.top - containerRect.top + scrollContainer.scrollTop;

          scrollContainer.scrollTo({
            top: relativeTop - 16,
            behavior: "smooth",
          });
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return undefined;
  }, []);

  const onSearchChange = useDebounce((e) =>
    setDebouncedSearchTerm(e.target.value),
  );

  const filteredData = useMemo(() => {
    if (!data || !debouncedSearchTerm.trim()) {
      return data || [];
    }

    const searchLower = debouncedSearchTerm.toLowerCase();

    return data
      .map((status) => {
        const statusNameMatches = status.name
          .toLowerCase()
          .includes(searchLower);

        if (statusNameMatches) {
          return status;
        }

        const filteredSubStatuses =
          status.subStatuses?.filter((sub) =>
            sub.name.toLowerCase().includes(searchLower),
          ) || [];

        if (filteredSubStatuses.length > 0) {
          return {
            ...status,
            subStatuses: filteredSubStatuses,
          };
        }

        return null;
      })
      .filter((status) => status !== null);
  }, [data, debouncedSearchTerm]);

  const renderSubStatuses = (status) => {
    return status.subStatuses.map((subStatus) => {
      const incomingCount = subStatus.incomingTransitionsCount;
      const outgoingCount = subStatus.outgoingTransitionsCount;

      const isSelected = isSameSubStatus(selectedSubStatus, subStatus);

      return (
        <SubStatusMenuItem
          key={getCandidateStatusKey(subStatus.statusId, subStatus.id)}
          isSubStatusDisabled={!subStatus.isActive}
          incomingTransitionsCount={incomingCount}
          outgoingTransitionsCount={outgoingCount}
          onClick={() => onSubStatusSelected(subStatus)}
          isSelected={isSelected}
        >
          {subStatus.name}
        </SubStatusMenuItem>
      );
    });
  };

  if (!data || data.length === 0) {
    return <div>No workflow data available</div>;
  }

  return (
    <Card bordered={false} bodyStyle={{ padding: 0 }}>
      <div style={{ padding: 16 }}>
        <Title level={5} style={{ fontWeight: 600 }} className="text-primary">
          Status List
        </Title>
        <Input.Search
          placeholder="Search"
          onChange={onSearchChange}
          defaultValue={debouncedSearchTerm}
          allowClear
        />
      </div>
      <Divider style={{ margin: 0 }} />
      <div
        ref={scrollContainerRef}
        className={`${styles.statusesContainer} scrollable-container`}
      >
        {filteredData.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={({ active }) => {
              if (active.id === assessmentStatusId) {
                setIsDragActive(true);
              }
            }}
            onDragEnd={(e) => {
              setIsDragActive(false);
              if (isDndEnabled) handleDragEnd(e);
            }}
            onDragCancel={() => setIsDragActive(false)}
          >
            <SortableContext
              items={data.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                {filteredData.map((s) => (
                  <SortableStatusItem
                    key={s.id}
                    status={s}
                    isAssessment={isDndEnabled && s.id === assessmentStatusId}
                    isBlocked={
                      isDragActive &&
                      (s.type === "final" ||
                        s.type === "hired" ||
                        s.id === data[0]?.id)
                    }
                    expandedStatusIds={expandedStatusIds}
                    onStatusExpanded={onStatusExpanded}
                    scrollRef={(el) => {
                      statusRefsMap.current[s.id] = el;
                    }}
                  >
                    {renderSubStatuses(s)}
                  </SortableStatusItem>
                ))}
              </Space>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </Card>
  );
};
